import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1e293b'
  },
  headerBanner: {
    backgroundColor: '#065f46',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#a7f3d0',
    marginTop: 2
  },
  badge: {
    backgroundColor: '#047857',
    color: '#ffffff',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 7.5,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  grid3: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#065f46',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.2,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 6.8,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 6.8,
    color: '#0f172a',
    width: '55%'
  },
  passText: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  failText: {
    color: '#b91c1c',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  signatureImage: {
    height: 32,
    maxWidth: 150,
    objectFit: 'contain',
    marginVertical: 2
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 5.8,
    color: '#64748b'
  }
});

const AirSourceHeatPumpHotWaterCylinderServiceMaintenancePDF = ({ formData = {} }) => {
  const business = formData.business || {};
  const job = formData.jobAddress || {};
  const landlord = formData.landlordAgent || {};
  const vessel = formData.vesselDetails || {};
  const heatPump = formData.heatPump || {};
  const operating = formData.operatingDetails || {};
  const defects = formData.defects || {};
  const work = formData.workCarriedOut || {};
  const issued = formData.issuedBy || {};
  const received = formData.receivedBy || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Banner */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Air Source Heat Pump & Cylinder Service Checklist</Text>
            <Text style={styles.headerSubtitle}>Periodic Maintenance & Statutory Compliance Service Record</Text>
          </View>
          <View style={styles.badge}>
            <Text>ANNUAL SERVICE</Text>
          </View>
        </View>

        {/* Addresses */}
        <View style={styles.grid3}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. Service Company</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Business:</Text>
              <Text style={styles.fieldValue}>{business.businessName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{business.businessAddress || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tel:</Text>
              <Text style={styles.fieldValue}>{business.businessTel || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Site Address</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Name:</Text>
              <Text style={styles.fieldValue}>{job.jobName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{job.jobAddress || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tel:</Text>
              <Text style={styles.fieldValue}>{job.jobTel || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>3. Client / Landlord</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Name:</Text>
              <Text style={styles.fieldValue}>{landlord.landlordName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{landlord.landlordAddress || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tel:</Text>
              <Text style={styles.fieldValue}>{landlord.landlordTel || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* Heat Pump & Cylinder Specifications */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>4. Heat Pump Unit Checks</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Make & Model:</Text>
              <Text style={styles.fieldValue}>{heatPump.make || 'N/A'} {heatPump.model || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Serial No. / Location:</Text>
              <Text style={styles.fieldValue}>{heatPump.serialNumber || 'N/A'} ({heatPump.location || 'Outdoor'})</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Evaporator Fins Cleaned:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{heatPump.evaporatorCleaned || 'Yes'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Condensate Drain Clear:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{heatPump.condensateClear || 'Yes'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Antivibration Mounts:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{heatPump.mountsSound || 'Satisfactory'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>5. Hot Water Cylinder Specification</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Make & Model:</Text>
              <Text style={styles.fieldValue}>{vessel.make || 'N/A'} {vessel.model || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Location & Capacity:</Text>
              <Text style={styles.fieldValue}>{vessel.location || 'Airing Cupboard'} ({vessel.capacity || '200'} L)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Makers Warning Label:</Text>
              <Text style={[styles.fieldValue, vessel.makersWarningLabel === 'Yes' ? styles.passText : styles.failText]}>
                {vessel.makersWarningLabel || 'Yes'}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Scale Reducer Fitted:</Text>
              <Text style={styles.fieldValue}>{vessel.scaleReducerFitted || 'Yes'}</Text>
            </View>
          </View>
        </View>

        {/* Operating Details & Safety Checks */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>6. Operating Pressures, Glycol & Safety Valves</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>System Pressure:</Text>
                <Text style={styles.fieldValue}>{operating.systemOperatingPressure || 1.5} Bar</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Expansion Vessel Pre-charge:</Text>
                <Text style={styles.fieldValue}>{operating.operatingPressureExpansionVessel || 1.5} Bar</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>PRV Lift Test:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{operating.prvLiftTest || 'Pass / Operating Correctly'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Glycol Freeze Point:</Text>
                <Text style={styles.fieldValue}>{operating.glycolFreezePoint || '-15°C (30% Propylene Glycol)'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>System pH / Inhibitor:</Text>
                <Text style={styles.fieldValue}>{operating.systemPh || '8.2'} (Inhibitor: OK)</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Tundish / D2 Discharge:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{operating.dischargeCompliant || 'Compliant (min 300mm fall)'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Defects & Work Done */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>7. Defects Noted</Text>
            <Text style={{ fontSize: 6.5, color: defects.defectsIdentified ? '#b91c1c' : '#475569' }}>
              {defects.defectsIdentified || 'No defects recorded. System operational within standard manufacturer parameters.'}
            </Text>
          </View>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>8. Work Carried Out</Text>
            <Text style={{ fontSize: 6.5, color: '#0f172a' }}>
              {work.workCarriedOut || 'Annual service completed: evaporator fins cleaned, glycol checked, magnetic filter cleaned, G3 relief valves tested.'}
            </Text>
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Service Engineer</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{issued.issuedByName || 'Certified Heat Pump Engineer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Accredited Service & Maintenance Stamp</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{issued.issuedByDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Customer / Client Receipt</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{received.receivedByName || 'Client / Resident'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Service checklist acknowledged</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{received.receivedByDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Heat Pump & Unvented Cylinder Annual Maintenance Checklist
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default AirSourceHeatPumpHotWaterCylinderServiceMaintenancePDF;
